import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/lib/auth";
import ProtectedRoute from "@/components/ProtectedRoute";
import Welcome from "@/pages/Welcome";
import Terms from "@/pages/Terms";
import Auth from "@/pages/Auth";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Home from "@/pages/Home";
import Profile from "@/pages/Profile";
import Garden from "@/pages/Garden";
import NewSpecimen from "@/pages/NewSpecimen";
import PlantDetail from "@/pages/PlantDetail";
import Beds from "@/pages/Beds";
import BedPlanner from "@/pages/BedPlanner";
import Stats from "@/pages/Stats";
import Almanack from "@/pages/Almanack";
import Catalogue from "@/pages/Catalogue";
import Codex from "@/pages/Codex";
import Community from "@/pages/Community";
import Database from "@/pages/Database";
import AdminCommunity from "@/pages/AdminCommunity";
import AdminContacts from "@/pages/AdminContacts";
import Admin from "@/pages/Admin";
import AdminUsers from "@/pages/AdminUsers";
import AdminLogin from "@/pages/AdminLogin";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import AppShell from "@/components/AppShell";
import { MumsAuthProvider } from "@/lib/mumsAuth";
import MumsLogin from "@/mums/MumsLogin";
import MumsShell from "@/mums/MumsShell";
import MumsHome from "@/mums/MumsHome";
import MumsTubers from "@/mums/MumsTubers";
import MumsBlooms from "@/mums/MumsBlooms";
import MumsFlowers from "@/mums/MumsFlowers";
import MumsSeeds from "@/mums/MumsSeeds";
import MumsJournal from "@/mums/MumsJournal";
import MumsGallery from "@/mums/MumsGallery";
import MumsDatabase from "@/mums/MumsDatabase";
import MumsSummary from "@/mums/MumsSummary";
import MumsBuilder from "@/mums/MumsBuilder";
import MumsWishlist from "@/mums/MumsWishlist";
import MumsTasks from "@/mums/MumsTasks";
import { KaylasAuthProvider } from "@/lib/kaylasAuth";
import KaylasLogin from "@/kaylas/KaylasLogin";
import KaylasShell from "@/kaylas/KaylasShell";
import KaylasHome from "@/kaylas/KaylasHome";
import KaylasTubers from "@/kaylas/KaylasTubers";
import KaylasBlooms from "@/kaylas/KaylasBlooms";
import KaylasFlowers from "@/kaylas/KaylasFlowers";
import KaylasSeeds from "@/kaylas/KaylasSeeds";
import KaylasJournal from "@/kaylas/KaylasJournal";
import KaylasGallery from "@/kaylas/KaylasGallery";
import KaylasDatabase from "@/kaylas/KaylasDatabase";
import KaylasSummary from "@/kaylas/KaylasSummary";
import KaylasBuilder from "@/kaylas/KaylasBuilder";
import KaylasWishlist from "@/kaylas/KaylasWishlist";
import KaylasTasks from "@/kaylas/KaylasTasks";
import BlackLeafSociety from "@/pages/BlackLeafSociety";
import BlackLeafCatalog from "@/pages/BlackLeafCatalog";

function App() {
  return (
    <div className="App paper min-h-screen">
      <BrowserRouter>
        <AuthProvider>
          <MumsAuthProvider>
          <KaylasAuthProvider>
          <Routes>
            <Route path="/" element={<Welcome />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            {/* Mum's Shed — private secret garden manager */}
            <Route path="/mums-shed/login" element={<MumsLogin />} />
            <Route path="/mums-shed" element={<MumsShell />}>
              <Route index element={<Navigate to="home" replace />} />
              <Route path="home" element={<MumsHome />} />
              <Route path="tubers" element={<MumsTubers />} />
              <Route path="blooms" element={<MumsBlooms />} />
              <Route path="flowers" element={<MumsFlowers />} />
              <Route path="seeds" element={<MumsSeeds />} />
              <Route path="journal" element={<MumsJournal />} />
              <Route path="gallery" element={<MumsGallery />} />
              <Route path="database" element={<MumsDatabase />} />
              <Route path="summary" element={<MumsSummary />} />
              <Route path="builder" element={<MumsBuilder />} />
              <Route path="wishlist" element={<MumsWishlist />} />
              <Route path="tasks" element={<MumsTasks />} />
            </Route>
            {/* Kayla's Garden — purple+green clone */}
            <Route path="/kaylas-garden/login" element={<KaylasLogin />} />
            <Route path="/kaylas-garden" element={<KaylasShell />}>
              <Route index element={<Navigate to="home" replace />} />
              <Route path="home" element={<KaylasHome />} />
              <Route path="tubers" element={<KaylasTubers />} />
              <Route path="blooms" element={<KaylasBlooms />} />
              <Route path="flowers" element={<KaylasFlowers />} />
              <Route path="seeds" element={<KaylasSeeds />} />
              <Route path="journal" element={<KaylasJournal />} />
              <Route path="gallery" element={<KaylasGallery />} />
              <Route path="database" element={<KaylasDatabase />} />
              <Route path="summary" element={<KaylasSummary />} />
              <Route path="builder" element={<KaylasBuilder />} />
              <Route path="wishlist" element={<KaylasWishlist />} />
              <Route path="tasks" element={<KaylasTasks />} />
            </Route>
            {/* Black Leaf Society — shared passphrase community log */}
            <Route path="/black-leaf-society" element={<BlackLeafSociety />} />
            <Route
              element={
                <ProtectedRoute>
                  <AppShell />
                </ProtectedRoute>
              }
            >
              <Route path="/home" element={<Home />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/garden" element={<Garden />} />
              <Route path="/garden/new" element={<NewSpecimen />} />
              <Route path="/garden/:id" element={<PlantDetail />} />
              <Route path="/beds" element={<Beds />} />
              <Route path="/beds/:id" element={<BedPlanner />} />
              <Route path="/stats" element={<Stats />} />
              <Route path="/almanack" element={<Almanack />} />
              <Route path="/codex" element={<Codex />} />
              <Route path="/catalogue" element={<Catalogue />} />
              <Route path="/black-leaf-catalog" element={<BlackLeafCatalog />} />
              <Route path="/community" element={<Community />} />
              <Route path="/database" element={<Database />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/admin/users" element={<AdminUsers />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/community" element={<AdminCommunity />} />
              <Route path="/admin/contact" element={<AdminContacts />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </KaylasAuthProvider>
          </MumsAuthProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster
        position="top-center"
        offset={80}
        toastOptions={{
          style: {
            background: "#FBF7EC",
            border: "1px solid #DDD3B8",
            color: "#2A2A22",
            fontFamily: "Inter, sans-serif",
          },
        }}
      />
    </div>
  );
}

export default App;
